import { useEffect, useRef, type FormEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * A side panel for editing one thing. It is attached to the page itself (not to the card it was opened from), so it always opens
 * in view, whatever the scroll position. Escape or the close button leaves; clicking outside does not, so nothing typed is lost by accident.
 */
export default function Drawer({ title, onClose, onSubmit, footer, children, width = 520 }: {
  title: string; onClose: () => void; onSubmit: () => void; footer: ReactNode; children: ReactNode; width?: number
}) {
  const panel = useRef<HTMLFormElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const html = document.documentElement
    const overflow = html.style.overflow
    html.style.overflow = 'hidden'
    panel.current?.querySelector<HTMLElement>('input, select, textarea')?.focus()
    return () => { html.style.overflow = overflow; previous?.focus?.() }
  }, [])
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit() }
  return createPortal(
    <div className="drawer-overlay" role="presentation">
      <form ref={panel} className="drawer" role="dialog" aria-modal="true" aria-label={title} style={{ width: `min(${width}px, 100%)` }} onSubmit={submit}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}>
        <header className="drawer-head">
          <h2>{title}</h2>
          <button type="button" className="drawer-x" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="drawer-body">{children}</div>
        <footer className="drawer-foot">{footer}</footer>
      </form>
    </div>,
    document.body,
  )
}
